import { supabase } from '../lib/supabase'
import { periodoAnterior, type Intervalo } from '../utils/periodo'

export type Plataforma = 'meta_ads' | 'google_ads'

export const PLATAFORMAS: Array<{ id: Plataforma; nome: string }> = [
  { id: 'meta_ads', nome: 'Meta Ads' },
  { id: 'google_ads', nome: 'Google Ads' },
]

/** Nome de exibição de qualquer plataforma da tabela `plataformas`. */
const NOMES_PLATAFORMA: Record<string, string> = {
  meta_ads: 'Meta Ads',
  google_ads: 'Google Ads',
  ga4: 'Google Analytics 4',
  search_console: 'Search Console',
  instagram: 'Instagram',
  facebook: 'Facebook',
}

export const nomePlataforma = (p: string) => NOMES_PLATAFORMA[p] ?? p

export type FiltroTrafego = {
  intervalo: Intervalo
  /** '' = todas as marcas visíveis */
  marcaId: string
  /** '' = as duas plataformas */
  plataforma: Plataforma | ''
  /** '' = todas as campanhas */
  campanhaId: string
}

export type Metricas = {
  investimento: number
  impressoes: number
  cliques: number
  conversoes: number
  valorConversoes: number
}

export type LinhaSerie = Metricas & { data: string; plataforma: Plataforma; demo: boolean }
export type Totais = Metricas & { demo: boolean }
export type LinhaCampanha = Metricas & {
  campanhaId: string
  campanha: string
  plataforma: Plataforma
  marcaId: string
  marca: string
}

export type DadosTrafego = {
  serie: LinhaSerie[]
  /** Totais do período anterior de mesmo tamanho, para o "vs período anterior". */
  anterior: Totais
  campanhas: LinhaCampanha[]
}

// O PostgREST pode devolver numeric como texto: tudo passa por Number().
type Bruto = Record<string, unknown>
const num = (v: unknown) => Number(v ?? 0)
const metricas = (r: Bruto): Metricas => ({
  investimento: num(r.investimento),
  impressoes: num(r.impressoes),
  cliques: num(r.cliques),
  conversoes: num(r.conversoes),
  valorConversoes: num(r.valor_conversoes),
})

async function rpc(fn: string, args: Record<string, unknown>): Promise<Bruto[]> {
  const { data, error } = await supabase.rpc(fn, args)
  if (error) throw new Error(error.message)
  return (data ?? []) as Bruto[]
}

const argsDoFiltro = (f: FiltroTrafego, intervalo: Intervalo, comCampanha = true) => ({
  p_inicio: intervalo.inicio,
  p_fim: intervalo.fim,
  p_marca: f.marcaId || null,
  p_plataforma: f.plataforma || null,
  ...(comCampanha ? { p_campanha: f.campanhaId || null } : {}),
})

/**
 * Tudo que o painel precisa, em três chamadas paralelas. As três vêm agregadas do banco
 * (o Supabase corta respostas em 1.000 linhas; linha a linha não escalaria).
 * A lista de campanhas ignora o filtro de campanha: é ela que alimenta esse filtro.
 */
export async function carregarTrafegoPago(f: FiltroTrafego): Promise<DadosTrafego> {
  const [serie, anterior, campanhas] = await Promise.all([
    rpc('trafego_pago_serie', argsDoFiltro(f, f.intervalo)),
    rpc('trafego_pago_totais', argsDoFiltro(f, periodoAnterior(f.intervalo))),
    rpc('trafego_pago_campanhas', argsDoFiltro(f, f.intervalo, false)),
  ])
  return {
    serie: serie.map((r) => ({
      ...metricas(r),
      data: String(r.data),
      plataforma: r.plataforma as Plataforma,
      demo: Boolean(r.demo),
    })),
    anterior: { ...metricas(anterior[0] ?? {}), demo: Boolean(anterior[0]?.demo) },
    campanhas: campanhas.map((r) => ({
      ...metricas(r),
      campanhaId: String(r.campanha_id),
      campanha: String(r.campanha),
      plataforma: r.plataforma as Plataforma,
      marcaId: String(r.marca_id),
      marca: String(r.marca),
    })),
  }
}
